import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AppError } from '@shared/api/error'
import {
  Banner,
  Button,
  Card,
  Drawer,
  ErrorState,
  Skeleton,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import type { StatusTone } from '@shared/design-system/ui/StatusBadge/StatusBadge'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { toast } from '@shared/lib/toast/toast-store'
import {
  configureCrmWebhook,
  crmWebhookKey,
  disableCrmWebhook,
  redeliverCrmWebhook,
  sendCrmWebhook,
  useCrmConfiguration,
  useCrmDeliveries,
} from '../api/crm-webhook.api'
import type { CrmDelivery } from '../api/crm-webhook.api'
import * as styles from './CrmWebhookPanel.css'

const statuses: Record<CrmDelivery['status'], { label: string; tone: StatusTone }> = {
  PENDING: { label: 'Na fila', tone: 'neutral' },
  SENDING: { label: 'Enviando', tone: 'informative' },
  DELIVERED: { label: 'Entregue', tone: 'success' },
  RETRYING: { label: 'Aguardando nova tentativa', tone: 'awaiting' },
  DEAD: { label: 'Interrompido', tone: 'closed' },
}
const failures = new Map([
  ['attempts_exhausted', 'O limite de tentativas foi atingido.'],
  ['paused', 'Envio pausado pela configuração da integração.'],
  ['http_error', 'O CRM não aceitou a entrega. Confira o serviço de destino.'],
  [
    'delivery_unavailable',
    'Não foi possível concluir a entrega. Confira a disponibilidade do destino.',
  ],
])
const date = (value: string) => {
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime())
    ? 'Data indisponível'
    : new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'America/Sao_Paulo',
      }).format(parsed)
}
type Confirmation =
  | { kind: 'configure'; url: string }
  | { kind: 'disable' }
  | { kind: 'redeliver'; id: string }

export function CrmWebhookPanel({ isSuper }: { isSuper: boolean }) {
  if (!isSuper)
    return (
      <Banner tone="info" title="Acesso restrito">
        A operação desta integração não está disponível para seu perfil.
      </Banner>
    )
  return <CrmWebhookOperations />
}

function CrmWebhookOperations() {
  const client = useQueryClient()
  const config = useCrmConfiguration()
  const deliveries = useCrmDeliveries(config.isSuccess)
  const [url, setUrl] = useState('')
  const [userId, setUserId] = useState('')
  const [urlError, setUrlError] = useState('')
  const [userError, setUserError] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [secret, setSecret] = useState<{ configId: string; value: string } | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const requestId = useRef<{ userId: string; configId: string; value: string } | null>(null)
  const urlRef = useRef<HTMLInputElement>(null)
  const userRef = useRef<HTMLInputElement>(null)
  const active = config.data?.enabled === true
  const visibleSecret = secret && secret.configId === config.data?.id ? secret.value : null
  const refresh = () => client.invalidateQueries({ queryKey: crmWebhookKey })

  function configurationRequest() {
    let valid = false
    try {
      const parsed = new URL(url.trim())
      valid =
        parsed.protocol === 'https:' &&
        !parsed.username &&
        !parsed.password &&
        url.trim().length <= 2048
    } catch {
      /* Invalid URL is handled next to the field. */
    }
    if (!valid) {
      setUrlError('Informe uma URL completa com HTTPS, sem usuário ou senha.')
      urlRef.current?.focus()
      return
    }
    setUrlError('')
    setConfirmation({ kind: 'configure', url: url.trim() })
  }

  async function confirm() {
    if (!confirmation || busy) return
    setBusy(true)
    const action = confirmation
    try {
      if (action.kind === 'configure') {
        setSecret(null)
        const result = await configureCrmWebhook(action.url)
        const { secret: value, ...safeConfiguration } = result
        client.setQueryData([...crmWebhookKey, 'configuration'], safeConfiguration)
        setSecret({ configId: result.id, value })
        setUrl('')
        toast.success({
          title: 'Conexão configurada',
          description: 'Guarde o segredo exibido para configurar o receptor do CRM.',
        })
      } else if (action.kind === 'disable') {
        await disableCrmWebhook()
        setSecret(null)
        toast.success({
          title: 'Envios desativados',
          description: 'Uma entrega já iniciada ainda pode terminar.',
        })
      } else {
        await redeliverCrmWebhook(action.id)
        toast.success({
          title: 'Reenvio solicitado',
          description: 'A entrega voltou à fila com o mesmo conteúdo e identificador.',
        })
      }
      setConfirmation(null)
      await refresh()
    } catch (error) {
      if (action.kind === 'configure') {
        applyApiFieldErrors(error, (field, feedback) => {
          if (field === 'url') setUrlError(feedback.message)
        })
        if (error instanceof AppError && error.status === 400)
          setUrlError('Confira a URL HTTPS e se o destino aceita conexões públicas.')
        setConfirmation(null)
        urlRef.current?.focus()
      }
      if (action.kind !== 'redeliver' && error instanceof AppError && error.status === 404)
        setUnavailable(true)
      toast.error(getUserFacingError(error, { title: 'Não foi possível concluir a operação' }))
      if (error instanceof AppError && error.status === 409) await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function send() {
    const configId = config.data?.id
    if (busy || !active || !configId) return
    const target = userId.trim()
    if (!target || target.length > 200) {
      setUserError('Informe o ID do vendedor, com até 200 caracteres.')
      userRef.current?.focus()
      return
    }
    setUserError('')
    setBusy(true)
    if (requestId.current?.userId !== target || requestId.current.configId !== configId) {
      requestId.current = { userId: target, configId, value: crypto.randomUUID() }
    }
    try {
      await sendCrmWebhook(target, requestId.current.value)
      requestId.current = null
      toast.success({
        title: 'Envio solicitado',
        description: 'A situação atual do vendedor será preparada para entrega ao CRM.',
      })
      await refresh()
    } catch (error) {
      toast.error(getUserFacingError(error, { title: 'Não foi possível solicitar o envio' }))
      if (error instanceof AppError && error.status === 409) await refresh()
    } finally {
      setBusy(false)
    }
  }

  if (
    unavailable ||
    (config.error instanceof AppError && config.error.status === 404) ||
    (deliveries.error instanceof AppError && deliveries.error.status === 404)
  )
    return (
      <section data-testid="crm-webhook-screen">
        <Banner tone="info" title="Integração indisponível">
          A integração com o CRM ainda não está disponível neste ambiente.
        </Banner>
        <Button
          variant="secondary"
          onClick={() => {
            setUnavailable(false)
            void refresh()
          }}
        >
          Verificar novamente
        </Button>
      </section>
    )
  if (
    (config.error instanceof AppError && config.error.status === 403) ||
    (deliveries.error instanceof AppError && deliveries.error.status === 403)
  )
    return (
      <Banner tone="info" title="Acesso restrito">
        Esta operação não está disponível para seu perfil.
      </Banner>
    )
  if (config.isPending)
    return (
      <div data-testid="crm-webhook-screen" role="status" aria-label="Carregando integração CRM">
        <Skeleton height={240} />
      </div>
    )
  if (config.isError)
    return (
      <ErrorState
        title="Não foi possível carregar a integração"
        description="Tente carregar os dados novamente."
        onRetry={() => void config.refetch()}
      />
    )

  return (
    <section
      className={styles.panel}
      data-testid="crm-webhook-screen"
      aria-label="Integração com o CRM"
    >
      <Card padding="md">
        <div className={styles.section}>
          <header className={styles.header}>
            <div className={styles.section}>
              <h2 className={styles.title}>Conexão com o CRM</h2>
              <p className={styles.copy}>
                Envie cadastro, perfil e situação operacional de KYC dos vendedores ao sistema de
                suporte.
              </p>
            </div>
            <StatusBadge
              label={
                active ? 'Envios ativos' : config.data ? 'Envios desativados' : 'Não configurado'
              }
              tone={active ? 'success' : 'neutral'}
            />
          </header>
          <p className={styles.copy}>
            Somente ID, nome, e-mail, telefone e situação operacional são enviados. Dados bancários
            e documentos ficam fora da integração.
          </p>
          {config.data ? (
            <p className={styles.copy}>
              Destino atual: <strong>{config.data.url}</strong>
            </p>
          ) : (
            <Banner tone="info" title="Conecte seu CRM">
              Cadastre uma URL pública HTTPS que receba os webhooks da Nova.
            </Banner>
          )}
          <form
            className={styles.form}
            data-testid="crm-webhook-configuration-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              configurationRequest()
            }}
          >
            {urlError ? (
              <Banner tone="danger" title="Revise o endereço">
                Corrija o campo destacado antes de continuar.
              </Banner>
            ) : null}
            <TextField
              ref={urlRef}
              label={config.data ? 'Nova URL do webhook' : 'URL do webhook'}
              type="url"
              placeholder="https://crm.suaempresa.com/webhooks/nova"
              value={url}
              maxLength={2048}
              error={urlError}
              onChange={(event) => {
                setUrl(event.target.value)
                setUrlError('')
              }}
              disabled={busy}
              helperText="O destino deve receber POSTs e validar a assinatura das entregas."
            />
            <div className={styles.actions}>
              <Button type="submit" disabled={busy} data-testid="crm-webhook-configure-btn">
                {config.data ? 'Criar nova conexão' : 'Configurar conexão'}
              </Button>
              {active ? (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setConfirmation({ kind: 'disable' })}
                  data-testid="crm-webhook-disable-btn"
                >
                  Desativar envios
                </Button>
              ) : null}
            </div>
            {config.data && !active ? (
              <p className={styles.copy}>
                Para voltar a enviar, crie uma nova conexão. Um novo segredo será gerado.
              </p>
            ) : null}
          </form>
          {visibleSecret ? (
            <div className={styles.section} data-testid="crm-webhook-secret">
              <Banner tone="warning" title="Guarde o segredo agora">
                Ele aparece somente nesta sessão. Compartilhe por um canal seguro com quem configura
                o receptor do CRM.
              </Banner>
              <code className={styles.secret} aria-label="Segredo de assinatura">
                {visibleSecret}
              </code>
              <Button
                variant="secondary"
                onClick={() => setSecret(null)}
                data-testid="crm-webhook-hide-secret-btn"
              >
                Já guardei, ocultar segredo
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
      <Card padding="md">
        <div className={styles.section}>
          <h2 className={styles.title}>Enviar um vendedor</h2>
          <p className={styles.copy}>
            Solicite o envio da situação atual de um vendedor. A confirmação abaixo significa
            entrada na fila, e não recebimento pelo CRM.
          </p>
          {!active ? (
            <p className={styles.copy}>Configure uma conexão ativa para solicitar envios.</p>
          ) : null}
          <form
            className={styles.form}
            data-testid="crm-webhook-send-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              void send()
            }}
          >
            {userError ? (
              <Banner tone="danger" title="Revise o vendedor">
                Corrija o campo destacado antes de continuar.
              </Banner>
            ) : null}
            <TextField
              ref={userRef}
              label="ID do vendedor"
              value={userId}
              maxLength={200}
              error={userError}
              disabled={busy || !active}
              onChange={(event) => {
                setUserId(event.target.value)
                setUserError('')
                requestId.current = null
              }}
              helperText="Use o identificador disponível no cadastro do vendedor."
            />
            <div className={styles.actions}>
              <Button type="submit" disabled={busy || !active} data-testid="crm-webhook-send-btn">
                {busy ? 'Aguarde...' : 'Solicitar envio'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
      <Card padding="md">
        <div className={styles.section} data-testid="crm-webhook-deliveries">
          <header className={styles.header}>
            <div>
              <h2 className={styles.title}>Histórico de entregas</h2>
              <p className={styles.copy}>
                Acompanhe o resultado de cada envio. Horários de Brasília.
              </p>
            </div>
            <Button
              variant="secondary"
              disabled={deliveries.isFetching || busy}
              onClick={() => void deliveries.refetch()}
              data-testid="crm-webhook-refresh-btn"
            >
              Atualizar histórico
            </Button>
          </header>
          {deliveries.isPending ? (
            <div role="status" aria-label="Carregando entregas">
              <Skeleton height={180} />
            </div>
          ) : deliveries.isError && !deliveries.data ? (
            <ErrorState
              title="Não foi possível carregar as entregas"
              onRetry={() => void deliveries.refetch()}
            />
          ) : (
            <>
              {deliveries.data?.pages.every((page) => page.items.length === 0) ? (
                <p className={styles.copy}>Nenhuma entrega registrada.</p>
              ) : (
                <div className={styles.grid}>
                  {deliveries.data?.pages
                    .flatMap((page) => page.items)
                    .map((item, index) => (
                      <article
                        key={item.id}
                        className={styles.item}
                        data-testid={`crm-webhook-row-${item.id}`}
                        aria-label={`Entrega ${index + 1}`}
                      >
                        <div className={styles.header}>
                          <strong>Entrega {index + 1}</strong>
                          <StatusBadge {...statuses[item.status]} />
                        </div>
                        <dl className={styles.details}>
                          <div className={styles.detail}>
                            <dt>Tentativas</dt>
                            <dd className={styles.value}>{item.attempts} de 8</dd>
                          </div>
                          <div className={styles.detail}>
                            <dt>Resposta HTTP</dt>
                            <dd className={styles.value}>{item.httpStatus ?? 'Sem resposta'}</dd>
                          </div>
                          <div className={styles.detail}>
                            <dt>Criada em</dt>
                            <dd className={styles.value}>{date(item.createdAt)}</dd>
                          </div>
                          <div className={styles.detail}>
                            <dt>Atualizada em</dt>
                            <dd className={styles.value}>{date(item.updatedAt)}</dd>
                          </div>
                        </dl>
                        {item.errorCode ? (
                          <p className={styles.copy}>
                            {failures.get(item.errorCode) ??
                              'O motivo não está disponível. Confira o serviço de destino.'}
                          </p>
                        ) : null}
                        {active &&
                        item.configId === config.data?.id &&
                        item.status !== 'SENDING' ? (
                          <Button
                            variant="secondary"
                            disabled={busy}
                            onClick={() => setConfirmation({ kind: 'redeliver', id: item.id })}
                            data-testid={`crm-webhook-redeliver-${item.id}-btn`}
                          >
                            Solicitar reenvio
                          </Button>
                        ) : (
                          <p className={styles.copy}>
                            {item.status === 'SENDING'
                              ? 'Aguarde o envio em andamento.'
                              : 'Reenvio indisponível para esta conexão.'}
                          </p>
                        )}
                      </article>
                    ))}
                </div>
              )}
              {deliveries.isError ? (
                <ErrorState
                  title="Não foi possível atualizar as entregas"
                  onRetry={() =>
                    void (deliveries.isFetchNextPageError
                      ? deliveries.fetchNextPage()
                      : deliveries.refetch())
                  }
                />
              ) : null}
              {deliveries.hasNextPage ? (
                <Button
                  variant="secondary"
                  disabled={deliveries.isFetching || busy}
                  onClick={() => void deliveries.fetchNextPage()}
                  data-testid="crm-webhook-load-more-btn"
                >
                  {deliveries.isFetchingNextPage ? 'Carregando...' : 'Carregar mais entregas'}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Card>
      <Drawer
        open={confirmation !== null}
        onClose={() => setConfirmation(null)}
        size="compact"
        variant="form"
        busy={busy}
        title={
          confirmation?.kind === 'configure'
            ? config.data
              ? 'Criar nova conexão?'
              : 'Configurar conexão?'
            : confirmation?.kind === 'disable'
              ? 'Desativar envios?'
              : 'Solicitar reenvio?'
        }
        footer={
          <div className={styles.actions}>
            <Button variant="secondary" disabled={busy} onClick={() => setConfirmation(null)}>
              Cancelar
            </Button>
            <Button
              disabled={busy}
              onClick={() => void confirm()}
              data-testid="crm-webhook-confirm-btn"
            >
              {busy ? 'Confirmando...' : 'Confirmar'}
            </Button>
          </div>
        }
      >
        <div className={styles.section} data-testid="crm-webhook-confirmation">
          {confirmation?.kind === 'configure' ? (
            <>
              <p className={styles.copy}>A Nova enviará os dados autorizados para:</p>
              <strong className={styles.copy}>{confirmation.url}</strong>
              <Banner tone="warning" title="Novo segredo de assinatura">
                Cada confirmação cria uma nova conexão e um novo segredo. Entregas da conexão
                anterior não serão transferidas para o novo destino.
              </Banner>
            </>
          ) : confirmation?.kind === 'disable' ? (
            <Banner tone="warning" title="Os novos envios serão interrompidos">
              Uma entrega já iniciada pode terminar. Para voltar a enviar, será necessário criar uma
              nova conexão.
            </Banner>
          ) : (
            <Banner tone="warning" title="O mesmo evento será enviado novamente">
              O reenvio preserva conteúdo e identificador. O CRM deve reconhecer duplicatas para
              evitar processamento repetido.
            </Banner>
          )}
        </div>
      </Drawer>
    </section>
  )
}
